import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getInvoices, getTrialInfo } from '../../services/api';

/**
 * PaymentGate — bloqueia acesso quando:
 *   1. Tenant tem fatura PENDENTE (escolheu plano mas não pagou)
 *   2. Trial EXPIRADO sem plano escolhido
 *
 * Paths liberados mesmo com bloqueio:
 *   - /app/billing (pagar fatura)
 *   - /app/choose-plan (escolher/trocar plano)
 *   - /app/settings (cancelar)
 */

const ALLOWED_PATHS = ['/app/billing', '/app/choose-plan', '/app/settings'];

export default function PaymentGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [blockReason, setBlockReason] = useState(null); // null | 'pending_invoice' | 'trial_expired'
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        // Check 1: fatura pendente
        const invRes = await getInvoices({ status: 'pending', limit: 1 }).catch(() => ({ data: {} }));
        const invoices = invRes.data?.invoices || [];
        if (invoices.length > 0) {
          if (!cancelled) {
            setInvoice(invoices[0]);
            setBlockReason('pending_invoice');
            setLoading(false);
          }
          return;
        }

        // Check 2: trial expirado (sem fatura = não escolheu plano)
        const trialRes = await getTrialInfo().catch(() => ({ data: {} }));
        const trial = trialRes.data?.trial_info;
        if (trial?.trial_expired) {
          if (!cancelled) {
            setBlockReason('trial_expired');
            setLoading(false);
          }
          return;
        }

        // Tudo OK — sem bloqueio
        if (!cancelled) {
          setBlockReason(null);
          setLoading(false);
        }
      } catch {
        if (!cancelled) { setBlockReason(null); setLoading(false); }
      }
    }

    check();
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Enquanto carrega, mostra children (evita flash)
  if (loading || !blockReason) return children;

  // Paths permitidos sempre
  if (ALLOWED_PATHS.some(p => location.pathname.startsWith(p))) return children;

  // ── Bloqueio: fatura pendente ──
  if (blockReason === 'pending_invoice') {
    const amount = invoice?.amount ? `R$ ${Number(invoice.amount).toFixed(2).replace('.', ',')}` : '';
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: 'calc(100vh - 120px)',
        padding: '40px 24px', textAlign: 'center',
      }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🔒</div>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--gray-900)', margin: '0 0 10px' }}>
          Pagamento pendente
        </h2>
        <p style={{ fontSize: 15, color: 'var(--gray-600)', maxWidth: 400, lineHeight: 1.6, margin: '0 0 24px' }}>
          Voce escolheu um plano mas ainda nao pagou.
          {amount && <> O valor de <strong>{amount}</strong> esta aguardando pagamento.</>}
          {' '}Pague via PIX pra liberar o acesso.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button className="btn btn-primary" style={{ padding: '12px 28px', fontSize: 15, fontWeight: 700 }}
            onClick={() => navigate('/app/billing')}>
            Pagar agora
          </button>
          <button className="btn btn-secondary" style={{ padding: '12px 20px', fontSize: 13 }}
            onClick={() => navigate('/app/choose-plan')}>
            Trocar plano
          </button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 20 }}>
          Apos o pagamento, o acesso e liberado automaticamente.
        </p>
      </div>
    );
  }

  // ── Bloqueio: trial expirado ──
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: 'calc(100vh - 120px)',
      padding: '40px 24px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>⏰</div>
      <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--gray-900)', margin: '0 0 10px' }}>
        Periodo gratuito encerrado
      </h2>
      <p style={{ fontSize: 15, color: 'var(--gray-600)', maxWidth: 420, lineHeight: 1.6, margin: '0 0 24px' }}>
        Seu periodo de teste chegou ao fim. Escolha um plano pra continuar usando o sistema.
        Seus dados estao salvos e nao serao perdidos.
      </p>
      <button className="btn btn-primary" style={{ padding: '14px 32px', fontSize: 16, fontWeight: 700 }}
        onClick={() => navigate('/app/choose-plan')}>
        Escolher plano
      </button>
      <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 20 }}>
        Voce pode trocar de plano ou cancelar a qualquer momento.
      </p>
    </div>
  );
}
